// ABOUTME: Camera controls module for the Hopp 3D scene
// ABOUTME: Wraps OrbitControls with touch support, bounds, and smooth transitions

import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import * as THREE from 'three';

let controls = null;
let camera = null;
let domElement = null;
let activeAnimations = new Set(); // Track active animation IDs
let animationIdCounter = 0;
let elevatedTargetLocks = 0;

// Configuration
const config = {
    // Zoom limits
    minDistance: 2,
    maxDistance: 150,

    // Vertical angle limits (prevent going underground)
    minPolarAngle: Math.PI / 4,            // ~45° from vertical - prevents top-down view for performance
    maxPolarAngle: Math.PI / 2.1,          // Just above horizon

    // Horizontal angle limits (optional, 0 = unlimited)
    minAzimuthAngle: -Infinity,
    maxAzimuthAngle: Infinity,

    // Pan limits (canvas boundaries)
    panBounds: {
        minX: -150,
        maxX: 150,
        minZ: -150,
        maxZ: 150
    },

    // Damping for smooth movement
    enableDamping: true,
    dampingFactor: 0.08,

    // Rotation speed
    rotateSpeed: 0.5,

    // Zoom speed
    zoomSpeed: 1.0,

    // Pan speed
    panSpeed: 0.8,

    // Touch settings (rotation disabled - pan/tilt only)
    touches: {
        ONE: THREE.TOUCH.PAN,
        TWO: THREE.TOUCH.DOLLY_PAN
    },

    // Mouse settings (rotation disabled - pan/tilt only)
    mouseButtons: {
        LEFT: THREE.MOUSE.PAN,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN
    },

    // Auto-rotate (disabled by default)
    autoRotate: false,
    autoRotateSpeed: 0.5
};

export function initCameraControls(cameraInstance, element, customConfig = {}) {
    camera = cameraInstance;
    domElement = element;

    // Merge custom config
    Object.assign(config, customConfig);

    // Create OrbitControls
    controls = new OrbitControls(camera, domElement);

    // Apply configuration
    controls.minDistance = config.minDistance;
    controls.maxDistance = config.maxDistance;
    controls.minPolarAngle = config.minPolarAngle;
    controls.maxPolarAngle = config.maxPolarAngle;
    controls.minAzimuthAngle = config.minAzimuthAngle;
    controls.maxAzimuthAngle = config.maxAzimuthAngle;

    controls.enableDamping = config.enableDamping;
    controls.dampingFactor = config.dampingFactor;

    controls.rotateSpeed = config.rotateSpeed;
    controls.zoomSpeed = config.zoomSpeed;
    controls.panSpeed = config.panSpeed;

    controls.touches = config.touches;
    controls.mouseButtons = config.mouseButtons;

    controls.autoRotate = config.autoRotate;
    controls.autoRotateSpeed = config.autoRotateSpeed;

    // Disable screen-space panning to keep pan on horizontal XZ plane
    // With tilted camera, screenSpacePanning=true causes "forward" pan to fight Y-clamp
    controls.screenSpacePanning = false;

    // Set initial target. The opening scene may aim slightly into the sky so
    // the sun flare is visible before user panning returns the target to y=0.
    const initialTarget = customConfig.initialTarget ?? { x: 0, y: 0, z: 0 };
    controls.target.set(initialTarget.x ?? 0, initialTarget.y ?? 0, initialTarget.z ?? 0);

    // Add pan bounds checking
    controls.addEventListener('change', enforcePanBounds);

    return controls;
}

function enforcePanBounds() {
    if (!controls || !config.panBounds) return;

    const target = controls.target;
    const bounds = config.panBounds;

    // Clamp target position
    target.x = THREE.MathUtils.clamp(target.x, bounds.minX, bounds.maxX);
    target.z = THREE.MathUtils.clamp(target.z, bounds.minZ, bounds.maxZ);

    // Keep user panning on the field, but let cinematic focus aim at flag height.
    if (elevatedTargetLocks === 0) {
        target.y = 0;
    }
}

function cancelAnimations(prefix) {
    // Cancel all animations matching the prefix
    for (const id of activeAnimations) {
        if (id.startsWith(prefix)) {
            activeAnimations.delete(id);
        }
    }
}

export function cancelAllAnimations() {
    activeAnimations.clear();
    // Re-enable damping in case it was disabled
    if (controls) controls.enableDamping = config.enableDamping;
}

// Page-load camera pose, captured by main.js after the initial position/target
// are set. `returnToInitialView` animates back to this snapshot — independent
// of wherever the user happens to be when called.
let initialPose = null;

export function setInitialPose(position, target) {
    initialPose = {
        position: new THREE.Vector3(position.x, position.y, position.z),
        target: new THREE.Vector3(target.x, target.y, target.z),
    };
}

export function returnToInitialView(durationMs = 1500) {
    if (!initialPose || !camera || !controls) return;
    cancelAnimations('focusReturn');
    cancelAnimations('initialReturn');

    const startPos = camera.position.clone();
    const startTarget = controls.target.clone();
    const animId = ++animationIdCounter;
    activeAnimations.add(`initialReturn-${animId}`);

    const startTime = performance.now();
    const wasDampingEnabled = controls.enableDamping;
    controls.enableDamping = false;

    function tick() {
        if (!activeAnimations.has(`initialReturn-${animId}`)) {
            controls.enableDamping = wasDampingEnabled;
            return;
        }
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / durationMs, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        camera.position.lerpVectors(startPos, initialPose.position, eased);
        controls.target.lerpVectors(startTarget, initialPose.target, eased);

        if (progress < 1) {
            requestAnimationFrame(tick);
        } else {
            activeAnimations.delete(`initialReturn-${animId}`);
            controls.enableDamping = wasDampingEnabled;
        }
    }

    requestAnimationFrame(tick);
}

/**
 * Cinematic intro tracking shot: orbit around the pitch centre — always looking
 * at the midfield point so the mountain panorama sweeps across the background —
 * then ease-settle into the initial broadcast framing. Runs once on load and
 * bails the moment the user grabs the camera.
 */
export function playIntroSweep({ durationMs = 4600, sweepDeg = 135 } = {}) {
    if (!initialPose || !camera || !controls) return;
    cancelAllAnimations();

    const target = initialPose.target.clone();
    const finalOffset = initialPose.position.clone().sub(target);
    const rF = Math.max(1e-3, finalOffset.length());
    const azF = Math.atan2(finalOffset.x, finalOffset.z);
    const polF = Math.acos(THREE.MathUtils.clamp(finalOffset.y / rF, -1, 1));
    // Start swept around, a touch higher and further out for a wide reveal.
    const azS = azF + THREE.MathUtils.degToRad(sweepDeg);
    const polS = Math.max(0.06, polF - THREE.MathUtils.degToRad(7));
    const rS = rF * 1.18;

    const animId = ++animationIdCounter;
    activeAnimations.add(`intro-${animId}`);
    const wasDamping = controls.enableDamping;
    controls.enableDamping = false;

    const place = (az, pol, r) => {
        const sinPol = Math.sin(pol);
        camera.position.set(
            target.x + r * sinPol * Math.sin(az),
            target.y + r * Math.cos(pol),
            target.z + r * sinPol * Math.cos(az),
        );
        controls.target.copy(target);
    };

    const cleanup = () => {
        controls.enableDamping = wasDamping;
        window.removeEventListener('pointerdown', onUserInput);
        window.removeEventListener('wheel', onUserInput);
    };
    function onUserInput() { activeAnimations.delete(`intro-${animId}`); cleanup(); }
    window.addEventListener('pointerdown', onUserInput, { once: true });
    window.addEventListener('wheel', onUserInput, { once: true });

    const startTime = performance.now();
    function tick() {
        if (!activeAnimations.has(`intro-${animId}`)) return;
        const t = Math.min((performance.now() - startTime) / durationMs, 1);
        const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // easeInOutCubic
        place(azS + (azF - azS) * e, polS + (polF - polS) * e, rS + (rF - rS) * e);
        if (t < 1) {
            requestAnimationFrame(tick);
        } else {
            activeAnimations.delete(`intro-${animId}`);
            camera.position.copy(initialPose.position);
            controls.target.copy(initialPose.target);
            cleanup();
        }
    }
    place(azS, polS, rS); // set the opening frame synchronously — no flash of the final pose
    requestAnimationFrame(tick);
}

// Bird's-eye auto-tilt: as the user zooms out, the camera's polar angle is
// clamped toward bird's-eye so short pennants stay visible at distance. At
// minDistance the broadcast-flat angle is kept; at maxDistance the camera
// must sit at least BIRDS_EYE_ELEVATION_DEG above horizon. Linearly
// interpolated. Animations (planted-pennant focus, returnToHome) bypass the
// clamp so cinematic shots can dip below it.
const BIRDS_EYE_POLAR_AT_FAR = Math.PI / 3; // 60° from vertical = 30° above horizon
const _autoTiltSpherical = new THREE.Spherical();
const _autoTiltOffset = new THREE.Vector3();

function clampPolarForDistance() {
    if (!controls || activeAnimations.size > 0) return;
    const distance = camera.position.distanceTo(controls.target);
    const range = config.maxDistance - config.minDistance;
    if (range <= 0) return;
    const t = Math.max(0, Math.min(1, (distance - config.minDistance) / range));
    const limitPolar = THREE.MathUtils.lerp(config.maxPolarAngle, BIRDS_EYE_POLAR_AT_FAR, t);

    _autoTiltOffset.subVectors(camera.position, controls.target);
    _autoTiltSpherical.setFromVector3(_autoTiltOffset);
    if (_autoTiltSpherical.phi > limitPolar) {
        _autoTiltSpherical.phi = limitPolar;
        _autoTiltOffset.setFromSpherical(_autoTiltSpherical);
        camera.position.copy(_autoTiltOffset).add(controls.target);
    }
}

export function update() {
    if (!controls) return;
    clampPolarForDistance();
    controls.update();
}

export function setTarget(x, y, z, animate = true) {
    if (!controls) return;

    // Cancel any existing target animations
    cancelAnimations('target');

    if (animate) {
        const animId = ++animationIdCounter;
        activeAnimations.add(`target-${animId}`);

        // Smooth transition to new target
        const start = controls.target.clone();
        const end = new THREE.Vector3(x, y, z);
        const duration = 2500; // ms - slow, contemplative pan
        const startTime = performance.now();

        function animateTarget() {
            // Check if this animation was cancelled
            if (!activeAnimations.has(`target-${animId}`)) return;

            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);

            controls.target.lerpVectors(start, end, eased);

            if (progress < 1) {
                requestAnimationFrame(animateTarget);
            } else {
                activeAnimations.delete(`target-${animId}`);
            }
        }

        animateTarget();
    } else {
        controls.target.set(x, y, z);
    }
}

export function setPosition(x, y, z, animate = true) {
    if (!camera) return;

    // Cancel any existing position animations
    cancelAnimations('position');

    if (animate) {
        const animId = ++animationIdCounter;
        activeAnimations.add(`position-${animId}`);

        // Temporarily disable damping to prevent OrbitControls from fighting
        const wasDampingEnabled = controls?.enableDamping;
        if (controls) controls.enableDamping = false;

        const start = camera.position.clone();
        const end = new THREE.Vector3(x, y, z);
        const duration = 2500; // ms - slow, contemplative zoom
        const startTime = performance.now();

        function animatePosition() {
            // Check if this animation was cancelled
            if (!activeAnimations.has(`position-${animId}`)) {
                if (controls && wasDampingEnabled) controls.enableDamping = true;
                return;
            }

            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            camera.position.lerpVectors(start, end, eased);

            if (progress < 1) {
                requestAnimationFrame(animatePosition);
            } else {
                activeAnimations.delete(`position-${animId}`);
                // Re-enable damping after animation completes
                if (controls && wasDampingEnabled) controls.enableDamping = true;
            }
        }

        animatePosition();
    } else {
        camera.position.set(x, y, z);
    }
}

export function zoomTo(distance, animate = true) {
    if (!camera || !controls) return;

    const direction = new THREE.Vector3()
        .subVectors(camera.position, controls.target)
        .normalize();

    const newPosition = new THREE.Vector3()
        .copy(controls.target)
        .add(direction.multiplyScalar(distance));

    setPosition(newPosition.x, newPosition.y, newPosition.z, animate);
}

export function focusOnPoint(x, z, zoomDistance = 30, animate = true) {
    // Move camera to focus on a specific point
    setTarget(x, 0, z, animate);

    if (animate) {
        setTimeout(() => zoomTo(zoomDistance, true), 500);
    } else {
        zoomTo(zoomDistance, false);
    }
}

export function focusOnCandleFromAbove(x, z, zoomDistance = 10, animate = true) {
    // Position camera at low angle, near ground level, looking slightly up
    // Creates intimate perspective with candle prominent in foreground
    const polarAngle = 1.1; // ~63 degrees from vertical (tilted slightly up from ground)
    const azimuthAngle = 0; // Looking from +Z toward -Z

    // Calculate camera position
    const offsetY = Math.cos(polarAngle) * zoomDistance;
    const horizontalDist = Math.sin(polarAngle) * zoomDistance;
    const offsetX = Math.sin(azimuthAngle) * horizontalDist;
    const offsetZ = Math.cos(azimuthAngle) * horizontalDist;

    const camX = x + offsetX;
    const camY = Math.max(offsetY, 2.0); // Keep camera at least 2 units high
    const camZ = z + offsetZ;

    // Keep target at ground level to avoid OrbitControls conflict
    setTarget(x, 0, z, animate);
    setPosition(camX, camY, camZ, animate);
}

export function resetView(animate = true) {
    setTarget(0, 0, 0, animate);
    setPosition(0, 50, 100, animate);
}

/**
 * Compute the world-space Y offset to apply to the focus look-at point so the
 * subject (a flag at world Y = subjectY) projects above the bottom HUD safe
 * area instead of at the canvas optical centre. Returns how far BELOW the
 * subject the look-at should sit; the caller subtracts it from subjectY.
 *
 * Math: with FOV 2θ and camera-to-target distance d, putting the subject at
 * NDC.y = Δ requires offsetting the look-at along world-up by
 *   ΔY = (Δ · d · tanθ) / (cos β + Δ · tanθ · sin β),
 * where β is the camera elevation above horizontal (cos β = √(1 − dir.y²)).
 * Exposed for unit testing.
 */
export function computeFocusLookAtYOffset({
    safeBottomPx,
    canvasHeight,
    fovDeg,
    zoomDistance,
    directionY,
}) {
    if (canvasHeight <= 0) return 0;
    const safeNdcY = Math.max(0, Math.min(0.95, safeBottomPx / canvasHeight));
    if (safeNdcY === 0) return 0;
    const tanFov = Math.tan((fovDeg * Math.PI) / 180 / 2);
    const dirY = Math.max(-0.99, Math.min(0.99, directionY));
    const cosPitch = Math.sqrt(1 - dirY * dirY);
    const sinPitch = Math.abs(dirY);
    const numerator = safeNdcY * tanFov * zoomDistance;
    const denominator = cosPitch + safeNdcY * tanFov * sinPitch;
    return numerator / denominator;
}

/**
 * How many bottom canvas-pixels to reserve so a subject aimed at the canvas
 * optical centre instead lands at the centre of the VISIBLE viewport.
 *
 * The canvas is locked to a 1:1 aspect ratio for the square Blick iframe. When
 * the host viewport is wider than tall the square canvas overflows below the
 * fold, so its optical centre (where the focus look-at projects) sits low. This
 * returns `2 · (canvasCentreY − viewportCentreY)` — the look-at helper raises the
 * subject by half of safeBottomPx, landing it at the visible centre. Only ever
 * raises (never pushes down): a fully-visible or centred canvas returns 0, so
 * desktop and mobile both work with no device-specific branch.
 */
export function computeVisibleCenterSafeBottomPx({ canvasTop, canvasHeight, viewportHeight }) {
    if (canvasHeight <= 0 || viewportHeight <= 0) return 0;
    const canvasCentreY = canvasTop + canvasHeight / 2;
    const raiseNeededPx = canvasCentreY - viewportHeight / 2;
    return Math.max(0, 2 * raiseNeededPx);
}

export function stabilizeFocusDirection(direction, minY = 0.18) {
    const stable = direction.clone().normalize();
    if (stable.y < minY) {
        stable.y = Math.max(Math.abs(stable.y), minY);
        stable.normalize();
    }
    return stable;
}

/**
 * Animate to focus on (x, z) at zoomDistance, hold briefly, then animate
 * back to wherever the camera was when called. Lighter than the candle-era
 * focusOnCandleThenReturn — no lock/unlock state machine, no tilt phase,
 * no home-position storage. Just a "look at the thing you just placed"
 * moment that leaves user-controlled limits untouched.
 *
 * `safeBottomPx` is canvas pixels reserved at the bottom for HUD overlays
 * (counter, etc.); the focus look-at is shifted down so the flag clears them.
 */
export function focusOnThenReturn(x, z, {
    zoomDistance = 12,
    targetY = 0,
    focusDurationMs = 1500,
    holdMs = 1800,
    returnDurationMs = 1800,
    safeBottomPx = 0,
    safeRightPx = 0,
    minY = 0.18,
    returnZoomDistance = null,
} = {}) {
    if (!camera || !controls) return;

    const startPos = camera.position.clone();
    const startTarget = controls.target.clone();

    // Hold the user's current camera-from-target direction, just zoom in
    // along it toward the new target. `minY` clamps the look-down angle —
    // lower values give a flatter, more tripod-like shot.
    const direction = stabilizeFocusDirection(new THREE.Vector3()
        .subVectors(startPos, startTarget)
        .normalize(), minY);

    // Push the look-at point downward in world space so the subject (flag at
    // world Y = targetY) projects above the bottom HUD, not at canvas centre.
    const canvasHeight = domElement?.clientHeight ?? 0;
    const canvasWidth = domElement?.clientWidth ?? 0;
    const lookAtYOffset = computeFocusLookAtYOffset({
        safeBottomPx,
        canvasHeight,
        fovDeg: camera.fov,
        zoomDistance,
        directionY: direction.y,
    });
    // Push the look-at horizontally so the subject lands in the LEFT half of
    // the viewport when the modal occupies the right. Shifts the target along
    // the camera's right-vector by Δx = (safeRightPx/canvasWidth) * 2·d·tan(fovX/2).
    let lookAtXOffset = 0;
    if (safeRightPx > 0 && canvasWidth > 0) {
        const aspect = canvasWidth / canvasHeight;
        const tanFovX = Math.tan((camera.fov * Math.PI) / 180 / 2) * aspect;
        const safeNdcX = Math.max(0, Math.min(0.85, safeRightPx / canvasWidth));
        lookAtXOffset = safeNdcX * tanFovX * zoomDistance;
    }
    // cross(worldUp, direction) gives the camera's right vector in world space.
    // direction points target→camera, so forward = -direction; right = cross(forward, up)
    // = cross(-direction, up) = cross(up, direction).
    const cameraRight = new THREE.Vector3()
        .crossVectors(new THREE.Vector3(0, 1, 0), direction)
        .normalize();
    const newTarget = new THREE.Vector3(x, targetY - lookAtYOffset, z)
        .add(cameraRight.clone().multiplyScalar(lookAtXOffset));

    const focusPos = new THREE.Vector3()
        .copy(newTarget)
        .add(direction.multiplyScalar(zoomDistance));

    // Optional cap on how far the return phase zooms back out. When set, the
    // camera ends up at `returnZoomDistance` from the original target along the
    // same orbit direction the user had — keeps freshly-planted pennants visible
    // rather than receding to the full far-orbit vantage point.
    let returnPos = startPos;
    let returnTarget = startTarget;
    if (Number.isFinite(returnZoomDistance) && returnZoomDistance > 0) {
        const startDir = new THREE.Vector3()
            .subVectors(startPos, startTarget)
            .normalize();
        const startDistance = startPos.distanceTo(startTarget);
        if (startDistance > returnZoomDistance) {
            returnPos = new THREE.Vector3()
                .copy(startTarget)
                .add(startDir.multiplyScalar(returnZoomDistance));
            // Keep the user's original look-at point so the orbit pivot doesn't
            // jump — only the zoom distance changes.
            returnTarget = startTarget;
        }
    }

    cancelAnimations('focusReturn');
    const animId = ++animationIdCounter;
    activeAnimations.add(`focusReturn-${animId}`);

    const wasDampingEnabled = controls.enableDamping;
    // Always lock the pan-bounds Y clamp during the animation — the cinematic
    // look-at can dip below the ground when the safe-area offset is large.
    elevatedTargetLocks++;
    controls.enableDamping = false;

    function finishFocusAnimation() {
        elevatedTargetLocks = Math.max(0, elevatedTargetLocks - 1);
        if (elevatedTargetLocks === 0) controls.target.y = 0;
        controls.enableDamping = wasDampingEnabled;
    }

    const phases = [
        { duration: focusDurationMs,  fromPos: startPos, toPos: focusPos, fromTarget: startTarget, toTarget: newTarget },
        { duration: holdMs,           fromPos: focusPos, toPos: focusPos, fromTarget: newTarget,   toTarget: newTarget },
        { duration: returnDurationMs, fromPos: focusPos, toPos: returnPos, fromTarget: newTarget,   toTarget: returnTarget },
    ];

    let phaseIndex = 0;
    let phaseStart = performance.now();

    function tick() {
        if (!activeAnimations.has(`focusReturn-${animId}`)) {
            finishFocusAnimation();
            return;
        }
        const phase = phases[phaseIndex];
        const elapsed = performance.now() - phaseStart;
        const progress = Math.min(elapsed / phase.duration, 1);
        const eased = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        camera.position.lerpVectors(phase.fromPos, phase.toPos, eased);
        controls.target.lerpVectors(phase.fromTarget, phase.toTarget, eased);
        controls.update();

        if (progress >= 1) {
            phaseIndex++;
            phaseStart = performance.now();
            if (phaseIndex >= phases.length) {
                activeAnimations.delete(`focusReturn-${animId}`);
                finishFocusAnimation();
                return;
            }
        }
        requestAnimationFrame(tick);
    }

    tick();
}

export function enableAutoRotate(speed = 0.5) {
    if (controls) {
        controls.autoRotate = true;
        controls.autoRotateSpeed = speed;
    }
}

export function disableAutoRotate() {
    if (controls) {
        controls.autoRotate = false;
    }
}

export function setZoomLimits(min, max) {
    if (controls) {
        controls.minDistance = min;
        controls.maxDistance = max;
    }
}

export function setPanBounds(minX, maxX, minZ, maxZ) {
    config.panBounds = { minX, maxX, minZ, maxZ };
}

export function enableControls() {
    if (controls) controls.enabled = true;
}

export function disableControls() {
    if (controls) controls.enabled = false;
}

export function getControls() {
    return controls;
}

export function dispose() {
    if (controls) {
        controls.removeEventListener('change', enforcePanBounds);
        controls.dispose();
        controls = null;
    }
}

// Set camera to bird-view (straight top-down)
export function setBirdView(targetX = 0, targetZ = 0, distance = 150) {
    if (!camera || !controls) return;

    // Temporarily allow top-down view
    controls.minPolarAngle = 0.01;

    // Position camera directly above target
    camera.position.set(targetX, distance, targetZ);
    controls.target.set(targetX, 0, targetZ);
    controls.update();
}

// Animate to bird-eye view (top-down) for overview
export function animateToBirdView(targetX = 0, targetZ = 0, distance = 150, onComplete = null) {
    if (!camera || !controls) return;

    const animId = ++animationIdCounter;
    activeAnimations.add(`birdView-${animId}`);

    // Unlock limits to allow zoom during animation
    unlockLimits();

    const startPos = camera.position.clone();
    const startTarget = controls.target.clone();
    const endPos = new THREE.Vector3(targetX, distance, targetZ);
    const endTarget = new THREE.Vector3(targetX, 0, targetZ);

    const duration = 2500;
    const startTime = performance.now();

    const wasDampingEnabled = controls.enableDamping;
    controls.enableDamping = false;

    function animate() {
        if (!activeAnimations.has(`birdView-${animId}`)) {
            if (wasDampingEnabled) controls.enableDamping = true;
            return;
        }

        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);

        camera.position.lerpVectors(startPos, endPos, eased);
        controls.target.lerpVectors(startTarget, endTarget, eased);

        // Gradually allow top-down view
        controls.minPolarAngle = THREE.MathUtils.lerp(config.minPolarAngle, 0.01, eased);
        controls.maxPolarAngle = THREE.MathUtils.lerp(Math.PI / 2, 0.01, eased);

        controls.update();

        if (progress < 1) {
            requestAnimationFrame(animate);
        } else {
            activeAnimations.delete(`birdView-${animId}`);
            controls.enableDamping = wasDampingEnabled;

            // Lock to new bird-view position
            lockedDistance = distance;
            lockedPolarAngle = 0.01;
            controls.minDistance = distance;
            controls.maxDistance = distance;
            controls.minPolarAngle = 0.01;
            controls.maxPolarAngle = 0.01;

            if (onComplete) onComplete();
        }
    }

    animate();
}

// Play intro animation: two-phase bird-view to angled view
// Phase 1 (0-60%): Zoom straight down from 150 to finalDistance
// Phase 2 (60-100%): Tilt from top-down to angled view
export function playIntroAnimation(targetX = 0, targetZ = 0, finalDistance = 28, onComplete = null) {
    if (!camera || !controls) return;

    // Disable user controls during animation
    controls.enabled = false;

    const animId = ++animationIdCounter;
    activeAnimations.add(`intro-${animId}`);

    const startY = camera.position.y; // Starting height (150)
    const endTarget = new THREE.Vector3(targetX, 0, targetZ);

    // Calculate end position: angled view at finalDistance (~72° from vertical)
    const polarAngle = Math.PI / 2.5; // ~72° from vertical for more horizontal intimate view
    const endY = Math.cos(polarAngle) * finalDistance;
    const horizontalDist = Math.sin(polarAngle) * finalDistance;

    const endPos = new THREE.Vector3(
        targetX,
        endY,
        targetZ + horizontalDist
    );

    // Top-down position at final distance (end of phase 1)
    const topDownPos = new THREE.Vector3(targetX, finalDistance, targetZ);

    const duration = 4000; // 4 seconds total
    const phase1End = 0.6; // Phase 1 takes 60% of time
    const startTime = performance.now();

    // Temporarily disable damping
    controls.enableDamping = false;

    // Easing function
    function easeInOutCubic(t) {
        return t < 0.5
            ? 4 * t * t * t
            : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function animateIntro() {
        if (!activeAnimations.has(`intro-${animId}`)) {
            controls.enabled = true;
            controls.enableDamping = config.enableDamping;
            return;
        }

        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        if (progress < phase1End) {
            // Phase 1: Zoom straight down (stay top-down)
            const zoomProgress = progress / phase1End;
            const zoomEased = easeInOutCubic(zoomProgress);

            // Interpolate Y position only, stay directly above target
            camera.position.set(
                targetX,
                THREE.MathUtils.lerp(startY, finalDistance, zoomEased),
                targetZ
            );
            controls.target.copy(endTarget);

            // Keep polar angle at minimum (top-down)
            controls.minPolarAngle = 0.01;
        } else {
            // Phase 2: Tilt to angled view
            const tiltProgress = (progress - phase1End) / (1 - phase1End);
            const tiltEased = easeInOutCubic(tiltProgress);

            // Interpolate from top-down position to angled position
            camera.position.lerpVectors(topDownPos, endPos, tiltEased);
            controls.target.copy(endTarget);

            // Transition polar angle limit
            controls.minPolarAngle = THREE.MathUtils.lerp(0.01, config.minPolarAngle, tiltEased);
        }

        controls.update();

        if (progress < 1) {
            requestAnimationFrame(animateIntro);
        } else {
            activeAnimations.delete(`intro-${animId}`);
            controls.enableDamping = config.enableDamping;
            controls.enabled = true;

            if (onComplete) onComplete();
        }
    }

    animateIntro();
}

// Home position storage for returning after candle focus
let homePosition = null;
let homeTarget = null;
let lockedDistance = null;
let lockedPolarAngle = null;

// Store current camera position as "home" (called after intro animation)
export function setHomePosition() {
    if (!camera || !controls) return;
    homePosition = camera.position.clone();
    homeTarget = controls.target.clone();
}

// Lock controls to pan-only (no zoom, no tilt) for USER input
// Also locks distance/polar limits for performance safety
export function lockToPanOnly() {
    if (!controls) return;

    // Store home position when locking
    setHomePosition();

    // Lock zoom by setting min/max to current distance
    lockedDistance = camera.position.distanceTo(controls.target);
    controls.minDistance = lockedDistance;
    controls.maxDistance = lockedDistance;

    // Lock tilt by setting min/max polar angle to current
    lockedPolarAngle = Math.acos(
        (camera.position.y - controls.target.y) / lockedDistance
    );
    controls.minPolarAngle = lockedPolarAngle;
    controls.maxPolarAngle = lockedPolarAngle;

    // Disable user zoom and rotate, but keep pan
    controls.enableRotate = false;
    controls.enableZoom = false;
    controls.enablePan = true;
}

// Temporarily unlock limits for programmatic camera movement
function unlockLimits() {
    if (!controls) return;
    controls.minDistance = config.minDistance;
    controls.maxDistance = config.maxDistance;
    controls.minPolarAngle = 0.01; // Allow any angle during animation
    controls.maxPolarAngle = Math.PI / 2;
}

// Restore locked limits after programmatic movement
function restoreLimits() {
    if (!controls || lockedDistance === null) return;
    controls.minDistance = lockedDistance;
    controls.maxDistance = lockedDistance;
    controls.minPolarAngle = lockedPolarAngle;
    controls.maxPolarAngle = lockedPolarAngle;
}

// Animate back to home position
export function returnToHome(animate = true, onComplete = null) {
    if (!camera || !controls || !homePosition || !homeTarget) return;

    // Unlock limits to allow zoom during animation
    unlockLimits();

    // Calculate home distance and polar angle for re-locking
    const homeDistance = homePosition.distanceTo(homeTarget);
    const homePolarAngle = Math.acos(
        (homePosition.y - homeTarget.y) / homeDistance
    );

    if (animate) {
        // Cancel any existing animations
        cancelAnimations('position');
        cancelAnimations('target');
        cancelAnimations('returnHome');

        const animId = ++animationIdCounter;
        activeAnimations.add(`returnHome-${animId}`);

        const startPos = camera.position.clone();
        const startTarget = controls.target.clone();
        const startPolarAngle = controls.minPolarAngle;
        const duration = 2000;
        const startTime = performance.now();

        // Temporarily disable damping
        const wasDampingEnabled = controls.enableDamping;
        controls.enableDamping = false;

        function animateReturn() {
            if (!activeAnimations.has(`returnHome-${animId}`)) {
                if (wasDampingEnabled) controls.enableDamping = true;
                return;
            }

            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);

            camera.position.lerpVectors(startPos, homePosition, eased);
            controls.target.lerpVectors(startTarget, homeTarget, eased);

            // Interpolate polar angle limits
            controls.minPolarAngle = THREE.MathUtils.lerp(startPolarAngle, homePolarAngle, eased);
            controls.maxPolarAngle = THREE.MathUtils.lerp(startPolarAngle, homePolarAngle, eased);

            controls.update();

            if (progress < 1) {
                requestAnimationFrame(animateReturn);
            } else {
                activeAnimations.delete(`returnHome-${animId}`);
                controls.enableDamping = wasDampingEnabled;

                // Re-lock to home position
                lockedDistance = homeDistance;
                lockedPolarAngle = homePolarAngle;
                controls.minDistance = homeDistance;
                controls.maxDistance = homeDistance;
                controls.minPolarAngle = homePolarAngle;
                controls.maxPolarAngle = homePolarAngle;

                if (onComplete) onComplete();
            }
        }

        animateReturn();
    } else {
        camera.position.copy(homePosition);
        controls.target.copy(homeTarget);

        // Re-lock to home position
        lockedDistance = homeDistance;
        lockedPolarAngle = homePolarAngle;
        controls.minDistance = homeDistance;
        controls.maxDistance = homeDistance;
        controls.minPolarAngle = homePolarAngle;
        controls.maxPolarAngle = homePolarAngle;

        controls.update();
        if (onComplete) onComplete();
    }
}

// Focus on candle, hold for duration (tilting to bird-eye), then return to home
export function focusOnCandleThenReturn(x, z, zoomDistance = 5, holdDurationMs = 5000) {
    if (!camera || !controls) return;

    // Temporarily unlock limits for programmatic movement
    unlockLimits();

    // First, focus on the candle
    focusOnCandleFromAbove(x, z, zoomDistance, true);

    // After focus animation completes (~2.5s), tilt to bird-eye during hold, then return
    const focusAnimationDuration = 2500;
    setTimeout(() => {
        // Animate tilt to bird-eye view during hold period for Danke readability
        tiltToBirdEye(x, z, zoomDistance, holdDurationMs, () => {
            returnToHome(true, () => {
                // Restore locked limits after returning home
                restoreLimits();
            });
        });
    }, focusAnimationDuration);
}

// Tilt from current angled view to bird-eye (top-down) while maintaining zoom distance
function tiltToBirdEye(targetX, targetZ, distance, durationMs, onComplete) {
    if (!camera || !controls) return;

    const animId = ++animationIdCounter;
    activeAnimations.add(`tiltBirdEye-${animId}`);

    const startPos = camera.position.clone();
    // End position: directly above target at same distance
    const endPos = new THREE.Vector3(targetX, distance, targetZ);

    const startTime = performance.now();

    // Temporarily disable damping
    const wasDampingEnabled = controls.enableDamping;
    controls.enableDamping = false;

    function animateTilt() {
        if (!activeAnimations.has(`tiltBirdEye-${animId}`)) {
            if (wasDampingEnabled) controls.enableDamping = true;
            return;
        }

        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / durationMs, 1);

        // Ease in-out for smooth transition
        const eased = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        camera.position.lerpVectors(startPos, endPos, eased);
        controls.target.set(targetX, 0, targetZ);
        controls.update();

        if (progress < 1) {
            requestAnimationFrame(animateTilt);
        } else {
            activeAnimations.delete(`tiltBirdEye-${animId}`);
            controls.enableDamping = wasDampingEnabled;
            if (onComplete) onComplete();
        }
    }

    animateTilt();
}
